/**
 * EngineConnectorPanel — shared Engine connection UI for Editor and Web.
 *
 * Layout structure:
 *   Header → StatusCard → ModeButtons → ModeSettings → Connect/Disconnect
 *
 * Apps pass their mode definitions, current state, and callbacks as props.
 * Tauri-specific features (directory picker in builtin mode) are injected via
 * the mode config's `settingsSlot` field.
 */

import {
    AlertCircle,
    CheckCircle2,
    Loader2,
    Power,
    PowerOff,
    RefreshCw,
    Wifi,
    WifiOff,
    XCircle,
} from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import type { ComponentType, ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import type { ThemeAccent } from "../theme/runtime/theme-accents";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";

/* ──────────────────────────── Types ──────────────────────────── */

export interface ConnectorModeConfig {
    /** Unique mode identifier (e.g. "dev", "builtin", "gateway", "local", "cloud", "none") */
    id: string;
    /** Display label (e.g. "内置", "Gateway", "本地", "云端", "断开") */
    label: string;
    /** Icon component for mode button and status card */
    icon: ComponentType<{ className?: string }>;
    /** Label when connected in this mode (e.g. "开发模式", "内置 Engine") */
    connectedLabel: string;
    /** Accent used for the status icon when connected */
    connectedAccent?: ThemeAccent;

    /**
     * If present, this mode shows an endpoint input field.
     * The object configures the input.
     */
    endpoint?: {
        placeholder: string;
        helpText?: ReactNode;
        /** Whether to show a probe/detect button next to the input */
        showProbe?: boolean;
    };

    /** Static help text shown when this mode is selected (no endpoint input) */
    helpText?: ReactNode;

    /**
     * Extra slot rendered after the standard mode settings.
     * Used for Tauri-specific UI like directory picker (builtin mode).
     */
    settingsSlot?: ReactNode;
}

export interface ConnectorPanelState {
    /** Currently active mode id */
    currentMode: string;
    /** Whether currently connected */
    connected: boolean;
    /** Error message */
    error: string | null;
    /** Currently attempting to connect */
    connecting: boolean;
    /** Progress message during connecting */
    connectMessage: string | null;
    /** Current endpoint value */
    endpoint: string;
    /** Engine status */
    status: { alive: boolean; ready: boolean };
    /** Probe result for the endpoint (null if never probed) */
    probeResult: { ready: boolean } | null;
    /** Whether probing is in progress */
    probing: boolean;
}

export interface ConnectorPanelCallbacks {
    onModeSwitch: (modeId: string) => void;
    onEndpointChange: (endpoint: string) => void;
    onConnect: (endpoint: string) => Promise<void>;
    onCancelConnect: () => void;
    onDisconnect: () => Promise<void>;
    onProbe: (endpoint: string) => Promise<void>;
    onRefreshStatus?: () => Promise<void>;
}

export interface EngineConnectorPanelProps {
    /** Available modes */
    modes: ConnectorModeConfig[];
    /** Current state from the store */
    state: ConnectorPanelState;
    /** Callbacks for user actions */
    callbacks: ConnectorPanelCallbacks;
    /** Hint text shown when disconnected */
    disconnectedHint?: ReactNode;
}

/* ──────────────────────────── Component ──────────────────────────── */

export function EngineConnectorPanel({
    modes,
    state,
    callbacks,
    disconnectedHint,
}: EngineConnectorPanelProps) {
    const t = useT("editor");
    const {
        currentMode,
        connected,
        error,
        connecting,
        connectMessage,
        endpoint,
        status,
        probeResult,
        probing,
    } = state;

    // Local endpoint mirrors store's endpoint for edit-then-commit pattern
    const [localEndpoint, setLocalEndpoint] = useState(endpoint);
    // Selected mode may differ from currentMode before connect
    const [selectedMode, setSelectedMode] = useState(currentMode);
    const [refreshingStatus, setRefreshingStatus] = useState(false);

    // Sync local endpoint with store
    useEffect(() => {
        setLocalEndpoint(endpoint);
    }, [endpoint]);

    // Sync selectedMode with currentMode
    useEffect(() => {
        setSelectedMode(currentMode);
    }, [currentMode]);

    const activeModeConfig = modes.find((m) => m.id === currentMode);
    const selectedModeConfig = modes.find((m) => m.id === selectedMode);

    const handleModeSwitch = useCallback(
        (modeId: string) => {
            setSelectedMode(modeId);
            callbacks.onModeSwitch(modeId);
        },
        [callbacks],
    );

    const handleConnect = useCallback(async () => {
        await callbacks.onConnect(localEndpoint);
    }, [callbacks, localEndpoint]);

    const handleDisconnect = useCallback(async () => {
        await callbacks.onDisconnect();
    }, [callbacks]);

    const handleProbe = useCallback(async () => {
        await callbacks.onProbe(localEndpoint);
    }, [callbacks, localEndpoint]);

    const handleRefreshStatus = useCallback(async () => {
        if (!callbacks.onRefreshStatus) {
            return;
        }
        setRefreshingStatus(true);
        try {
            await callbacks.onRefreshStatus();
        } finally {
            setRefreshingStatus(false);
        }
    }, [callbacks]);

    return (
        <div className="flex flex-col gap-3 p-3 text-sm">
            {/* Header */}
            <div className="flex items-center gap-2 font-medium text-foreground">
                <Wifi className="w-4 h-4" />
                <span>{t("connector.title")}</span>
            </div>

            {/* Error Display */}
            {error ? (
                <div
                    className="flex items-start gap-2 rounded-md border p-2 text-xs theme-accent-banner"
                    style={getThemeAccentStyle("error")}
                >
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            ) : null}

            {/* Status Card */}
            <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="flex items-center gap-2 mb-2">
                    {connected && activeModeConfig ? (
                        <span
                            className="theme-accent-text"
                            style={getThemeAccentStyle(
                                activeModeConfig.connectedAccent ?? "success",
                            )}
                        >
                            <activeModeConfig.icon className="w-4 h-4" />
                        </span>
                    ) : (
                        <WifiOff className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span className="font-medium">
                        {connected && activeModeConfig
                            ? activeModeConfig.connectedLabel
                            : t("connector.notConnected")}
                    </span>
                    {connected ? (
                        <div className="ml-auto flex items-center gap-1.5">
                            {callbacks.onRefreshStatus ? (
                                <button
                                    type="button"
                                    className="flex h-6 w-6 items-center justify-center rounded-md border border-border/60 text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/50"
                                    onClick={() => {
                                        void handleRefreshStatus();
                                    }}
                                    disabled={refreshingStatus}
                                    title={t("connector.action.refreshStatus")}
                                >
                                    <RefreshCw
                                        className={`w-3 h-3 ${refreshingStatus ? "animate-spin" : ""}`}
                                    />
                                </button>
                            ) : null}
                            <span className="flex items-center gap-1 text-xs">
                                {status.ready ? (
                                    <>
                                        <CheckCircle2
                                            className="w-3 h-3 theme-accent-text"
                                            style={getThemeAccentStyle("success")}
                                        />
                                        <span
                                            className="theme-accent-text"
                                            style={getThemeAccentStyle("success")}
                                        >
                                            {t("connector.status.ready")}
                                        </span>
                                    </>
                                ) : status.alive ? (
                                    <>
                                        <Loader2
                                            className="w-3 h-3 animate-spin theme-accent-text"
                                            style={getThemeAccentStyle("warning")}
                                        />
                                        <span
                                            className="theme-accent-text"
                                            style={getThemeAccentStyle("warning")}
                                        >
                                            {t("connector.status.starting")}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <XCircle
                                            className="w-3 h-3 theme-accent-text"
                                            style={getThemeAccentStyle("error")}
                                        />
                                        <span
                                            className="theme-accent-text"
                                            style={getThemeAccentStyle("error")}
                                        >
                                            {t("connector.disconnected")}
                                        </span>
                                    </>
                                )}
                            </span>
                        </div>
                    ) : null}
                </div>

                {/* Connected description */}
                {connected && activeModeConfig?.endpoint ? (
                    <div className="text-xs text-muted-foreground font-mono truncate">
                        {endpoint}
                    </div>
                ) : connected && activeModeConfig?.helpText ? (
                    <div className="text-xs text-muted-foreground">
                        {activeModeConfig.helpText}
                    </div>
                ) : !connected && disconnectedHint ? (
                    <div className="text-xs text-muted-foreground">
                        {disconnectedHint}
                    </div>
                ) : null}
            </div>

            {/* Mode Buttons */}
            <div className="flex rounded-md border border-border overflow-hidden">
                {modes.map((mode, index) => (
                    <div key={mode.id} className="contents">
                        {index > 0 ? <div className="w-px bg-border" /> : null}
                        <button
                            type="button"
                            className={`flex-1 flex flex-col items-center justify-center gap-1 px-2 py-2.5 text-[10px] font-medium transition-colors ${selectedMode === mode.id
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                                }`}
                            onClick={() => handleModeSwitch(mode.id)}
                        >
                            <mode.icon className="w-4 h-4" />
                            {mode.label}
                        </button>
                    </div>
                ))}
            </div>

            {/* Mode-specific settings */}
            {selectedModeConfig?.endpoint ? (
                <div className="space-y-2">
                    {selectedModeConfig.endpoint.helpText ? (
                        <div className="text-xs text-muted-foreground">
                            {selectedModeConfig.endpoint.helpText}
                        </div>
                    ) : null}
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={localEndpoint}
                            onChange={(e) => {
                                setLocalEndpoint(e.target.value);
                                callbacks.onEndpointChange(e.target.value);
                            }}
                            placeholder={selectedModeConfig.endpoint.placeholder}
                            className="flex-1 px-2 py-1.5 text-xs rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground font-mono"
                        />
                        {selectedModeConfig.endpoint.showProbe ? (
                            <button
                                type="button"
                                className="flex items-center justify-center px-2 py-1.5 rounded-md border border-border bg-muted text-muted-foreground hover:bg-muted/80 transition-colors disabled:opacity-50"
                                onClick={handleProbe}
                                disabled={probing}
                                title={t("connector.action.probe")}
                            >
                                {probing ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                    <RefreshCw className="w-3.5 h-3.5" />
                                )}
                            </button>
                        ) : null}
                    </div>
                    {probeResult ? (
                        <div className="flex items-center gap-1.5 text-xs">
                            {probeResult.ready ? (
                                <>
                                    <CheckCircle2
                                        className="w-3.5 h-3.5 theme-accent-text"
                                        style={getThemeAccentStyle("success")}
                                    />
                                    <span
                                        className="theme-accent-text"
                                        style={getThemeAccentStyle("success")}
                                    >
                                        {t("connector.detected")}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <XCircle
                                        className="w-3.5 h-3.5 theme-accent-text"
                                        style={getThemeAccentStyle("warning")}
                                    />
                                    <span
                                        className="theme-accent-text"
                                        style={getThemeAccentStyle("warning")}
                                    >
                                        {t("connector.notReady")}
                                    </span>
                                </>
                            )}
                        </div>
                    ) : null}
                </div>
            ) : selectedModeConfig?.helpText && !connected ? (
                <div className="text-xs text-muted-foreground">
                    {selectedModeConfig.helpText}
                </div>
            ) : null}

            {/* Mode-specific extra settings slot */}
            {selectedModeConfig?.settingsSlot ?? null}

            {/* Connect / Disconnect / Cancel */}
            {selectedMode !== "none" ? (
                connecting ? (
                    <div className="space-y-2">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                            <span>{connectMessage ?? t("connector.status.connecting")}</span>
                        </div>
                        <button
                            type="button"
                            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors border theme-accent-button"
                            style={getThemeAccentStyle("error")}
                            onClick={callbacks.onCancelConnect}
                        >
                            <XCircle className="w-3.5 h-3.5" />
                            {t("connector.action.cancel")}
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${connected
                                ? "border theme-accent-button"
                                : "bg-primary text-primary-foreground hover:bg-primary/90"
                            }`}
                        style={connected ? getThemeAccentStyle("error") : undefined}
                        onClick={connected ? handleDisconnect : handleConnect}
                    >
                        {connected ? (
                            <>
                                <PowerOff className="w-3.5 h-3.5" />
                                {t("connector.action.disconnect")}
                            </>
                        ) : (
                            <>
                                <Power className="w-3.5 h-3.5" />
                                {t("connector.action.connect")}
                            </>
                        )}
                    </button>
                )
            ) : null}
        </div>
    );
}
